<template>
  <div class="kb-page">
    <AppHeader />

    <!-- Hero Section -->
    <section class="kb-hero">
      <div class="container-xl kb-hero__inner">

        <!-- LEFT: Text -->
        <div class="kb-hero__left" data-aos="fade-right">
          <div class="kb-hero__badge">
            <i class="bi bi-patch-check-fill me-2"></i>India's Trusted Manufacturer
          </div>
          <h1>
            Industrial Protection<br>
            <span>Built to Last.</span>
          </h1>
          <p>
            Premium HDPE, PVC-FR &amp; Canvas Tarpaulins, Agro Shade Nets and Nylon Ropes — engineered for farms, industries, and households across India.
          </p>
          <div class="kb-hero__actions">
            <a href="https://wa.me/919892310123?text=Hello%2C%20I%20would%20like%20to%20request%20a%20quote" target="_blank" class="kb-btn kb-btn--navy">
              <i class="bi bi-whatsapp me-2"></i>Request a Quote
            </a>
            <router-link to="/products" class="kb-btn kb-btn--outline">
              Explore Products <i class="bi bi-arrow-right ms-2"></i>
            </router-link>
          </div>
          <!-- Product category pills -->
          <div class="kb-hero__pills">
            <span><i class="bi bi-check2-circle me-1"></i>HDPE Tarpaulins</span>
            <span><i class="bi bi-check2-circle me-1"></i>PVC-FR Tarpaulins</span>
            <span><i class="bi bi-check2-circle me-1"></i>Canvas Tarpaulin</span>
            <span><i class="bi bi-check2-circle me-1"></i>Agro Shade Nets</span>
            <span><i class="bi bi-check2-circle me-1"></i>Nylon Ropes</span>
          </div>
        </div>

        <!-- RIGHT: Image -->
        <div class="kb-hero__right" data-aos="fade-left" data-aos-delay="120">
          <div class="kb-hero__img-frame">
            <!-- Decorative blobs -->
            <div class="kb-hero__blob kb-hero__blob--1"></div>
            <div class="kb-hero__blob kb-hero__blob--2"></div>
            <img src="@/assets/slider3.webp" alt="KeBee Industries Products" />
            <!-- Floating badge -->
            <div class="kb-hero__float-badge">
              <i class="bi bi-award-fill"></i>
              <div>
                <strong>Premium Quality</strong>
                <span>100% Virgin Polymer</span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>

    <!-- Stats Strip -->
    <section class="kb-stats">
      <div class="container-xl">
        <div class="kb-stats__grid">
          <div class="kb-stats__item" v-for="stat in stats" :key="stat.label">
            <div class="kb-stats__icon"><i :class="stat.icon"></i></div>
            <div class="kb-stats__num">{{ stat.value }}</div>
            <div class="kb-stats__label">{{ stat.label }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Products Preview -->
    <section class="kb-section kb-section--light" id="products">
      <div class="container-xl">
        <div class="kb-section-header" data-aos="fade-up">
          <span class="kb-section-header__tag">What We Make</span>
          <h2>Our Product Range</h2>
          <p>Durable, versatile, and manufactured to the highest standards — tailored for every industry need</p>
        </div>
        <div class="kb-products-grid">
          <div
            v-for="(product, idx) in products"
            :key="product.id"
            class="kb-product-card"
            data-aos="fade-up"
            :data-aos-delay="(idx % 3) * 100"
            style="cursor:pointer"
            @click="$router.push(`/products/${product.slug}`)"
          >
            <div class="kb-product-card__img">
              <img :src="product.heroImage" :alt="product.name" loading="lazy" />
              <span class="kb-product-card__badge" :style="{ background: product.badgeColor }">{{ product.badge }}</span>
            </div>
            <div class="kb-product-card__body">
              <span class="kb-product-card__cat">{{ product.category }}</span>
              <h3>{{ product.name }}</h3>
              <p>{{ product.shortDesc }}</p>
              <router-link :to="`/products/${product.slug}`" class="kb-product-card__link">
                View Details <i class="bi bi-arrow-right ms-1"></i>
              </router-link>
            </div>
          </div>
        </div>
        <div class="text-center mt-5" data-aos="fade-up">
          <router-link to="/products" class="kb-btn kb-btn--navy">
            View All Products <i class="bi bi-grid-3x3-gap ms-2"></i>
          </router-link>
        </div>
      </div>
    </section>

    <!-- About Snippet -->
    <section class="kb-section" id="about-home">
      <div class="container-xl">
        <div class="kb-about-home">
          <div class="kb-about-home__images" data-aos="fade-right">
            <div class="kb-about-home__img-main">
              <img src="@/assets/about-bg.webp" alt="KeBee Tarpaulin" loading="lazy" />
            </div>
            <div class="kb-about-home__img-sub">
              <img src="@/assets/slider2.webp" alt="KeBee Products" loading="lazy" />
            </div>
            <div class="kb-about-home__img-sub">
              <img src="@/assets/slider1.webp" alt="KeBee Tarpaulin Range" loading="lazy" />
              <div class="kb-about-home__img-badge">
                <span class="num"><i class="bi bi-award-fill"></i></span>
                <span class="lbl">Trusted<br>Quality</span>
              </div>
            </div>
          </div>
          <div class="kb-about-home__content" data-aos="fade-left" data-aos-delay="100">
            <span class="kb-section-header__tag">Who We Are</span>
            <h2>The KeBee Blueprint</h2>
            <p class="lead-text">At KeBee Tarpaulins, we are more than just manufacturers; we are the frontline of protection for your most valuable assets.</p>
            <p>With a foundation built on industrial expertise and a passion for material science, KeBee has emerged as a premier provider of heavy-duty covering solutions. From the rugged fields of agriculture to the high-stakes environment of industrial logistics, our diverse product range is designed to withstand the toughest conditions.</p>
            <div class="kb-about-home__pillars">
              <div v-for="pillar in pillars" :key="pillar.title" class="kb-pillar">
                <div class="kb-pillar__icon"><i :class="pillar.icon"></i></div>
                <div>
                  <h4>{{ pillar.title }}</h4>
                  <p>{{ pillar.desc }}</p>
                </div>
              </div>
            </div>
            <router-link to="/about" class="kb-btn kb-btn--navy mt-4">
              Our Full Story <i class="bi bi-arrow-right ms-2"></i>
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- Why KeBee -->
    <section class="kb-section kb-section--light">
      <div class="container-xl">
        <div class="kb-section-header" data-aos="fade-up">
          <span class="kb-section-header__tag">The KeBee Edge</span>
          <h2>Why Industry Leaders Choose Us</h2>
          <p>In a market flooded with recycled, low-grade alternatives, KeBee stands alone</p>
        </div>
        <div class="kb-why-grid">
          <div v-for="(item, i) in whyItems" :key="item.title" class="kb-why-card" data-aos="fade-up" :data-aos-delay="i * 80">
            <div class="kb-why-card__num">0{{ i + 1 }}</div>
            <div class="kb-why-card__icon"><i :class="item.icon"></i></div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Blog Preview -->
    <section class="kb-section">
      <div class="container-xl">
        <div class="kb-section-header" data-aos="fade-up">
          <span class="kb-section-header__tag">Knowledge Hub</span>
          <h2>Latest from Our Blog</h2>
          <p>Expert insights on tarpaulins, agro nets, and industrial protection</p>
        </div>
        <div class="kb-blog-preview">
          <div
            v-for="(post, idx) in recentBlogs"
            :key="post.id"
            class="kb-blog-card"
            data-aos="fade-up"
            :data-aos-delay="idx * 100"
            style="cursor:pointer"
            @click="$router.push(`/blog/${post.slug}`)"
          >
            <div class="kb-blog-card__img">
              <img :src="post.coverImage" :alt="post.title" loading="lazy" />
              <span class="kb-blog-card__cat" :style="{ background: post.categoryColor }">{{ post.category }}</span>
            </div>
            <div class="kb-blog-card__body">
              <div class="kb-blog-card__meta">
                <span><i class="bi bi-calendar3 me-1"></i>{{ post.date }}</span>
                <span><i class="bi bi-clock me-1"></i>{{ post.readTime }}</span>
              </div>
              <h3>{{ post.title }}</h3>
              <p>{{ post.excerpt }}</p>
              <router-link :to="`/blog/${post.slug}`" class="kb-blog-card__link">
                Read More <i class="bi bi-arrow-right ms-1"></i>
              </router-link>
            </div>
          </div>
        </div>
        <div class="text-center mt-5" data-aos="fade-up">
          <router-link to="/blog" class="kb-btn kb-btn--navy">
            View All Articles <i class="bi bi-arrow-right ms-2"></i>
          </router-link>
        </div>
      </div>
    </section>

    <!-- CTA Banner -->
    <section class="kb-cta-banner">
      <div class="container-xl">
        <div class="kb-cta-banner__content" data-aos="fade-up">
          <h2>Ready to Get the Best Protection for Your Assets?</h2>
          <p>Custom sizes, bulk orders, and pan-India delivery. Get your price list today.</p>
          <div class="kb-cta-banner__actions">
            <a href="https://wa.me/919892310123?text=Hello%2C%20I%20would%20like%20to%20request%20a%20quote" target="_blank" class="kb-btn kb-btn--primary">
              <i class="bi bi-whatsapp me-2"></i>WhatsApp Us Now
            </a>
            <a href="mailto:info@kebeeindustries.com" class="kb-btn kb-btn--outline-white">
              <i class="bi bi-envelope me-2"></i>Email Enquiry
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- Contact Strip -->
    <section class="kb-contact-strip">
      <div class="container-xl">
        <div class="kb-contact-strip__grid">
          <a href="Tel:+919892310123" class="kb-contact-item">
            <i class="bi bi-telephone-fill"></i>
            <div>
              <span>Call Us</span>
              <strong>+91 98923 10123</strong>
            </div>
          </a>
          <a href="mailto:info@kebeeindustries.com" class="kb-contact-item">
            <i class="bi bi-envelope-fill"></i>
            <div>
              <span>Email Us</span>
              <strong>info@kebeeindustries.com</strong>
            </div>
          </a>
          <a href="https://wa.me/919892310123" target="_blank" class="kb-contact-item">
            <i class="bi bi-whatsapp"></i>
            <div>
              <span>WhatsApp</span>
              <strong>Chat with Us</strong>
            </div>
          </a>
          <div class="kb-contact-item kb-contact-item--address">
            <i class="bi bi-geo-alt-fill"></i>
            <div>
              <span>Visit Us</span>
              <strong>Kandivali West, Mumbai</strong>
            </div>
          </div>
        </div>
      </div>
    </section>

    <AppFooter />
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import { setSeo } from '@/utils/seo.js'
import { products } from '@/data/products.js'
import { blogs } from '@/data/blogs.js'

export default {
  name: 'HomePage',
  components: { AppHeader, AppFooter },
  mounted() {
    setSeo({
      title: 'KeBee Industries – Premium Tarpaulins, Agro Nets & Ropes | Mumbai',
      description: 'KeBee Industries manufactures HDPE Tarpaulins, PVC Fire-Retardant Tarpaulins, Canvas Tarpaulins, Agro Shade Nets & Nylon Ropes in Mumbai. 10+ years experience, 5K+ happy customers. Pan-India delivery.',
      path: '/',
      keywords: 'tarpaulin manufacturer Mumbai, HDPE tarpaulin, PVC FR tarpaulin, agro shade net, canvas tarpaulin, nylon rope, KeBee Industries'
    })
  },
  data() {
    return {
      products,
      stats: [
        { icon: 'bi bi-patch-check-fill', value: '100%', label: 'Proven Quality' },
        { icon: 'bi bi-people-fill', value: '5K+', label: 'Happy Customers' },
        { icon: 'bi bi-box-seam-fill', value: '6', label: 'Product Categories' },
        { icon: 'bi bi-truck-front-fill', value: 'Pan-India', label: 'Delivery Network' },
      ],
      pillars: [
        { icon: 'bi bi-shield-lock-fill', title: '100% Virgin Quality Polymers', desc: 'No recycled material. Every product starts with 100% Virgin HDPE or PVC for superior performance.' },
        { icon: 'bi bi-gear-fill', title: 'Custom Sizes & Bulk Orders', desc: 'Flexible manufacturing to meet any dimension or quantity requirement for residential or industrial needs.' },
        { icon: 'bi bi-patch-check-fill', title: 'Rigorous Quality Testing', desc: 'Hydrostatic pressure, UV resistance, and tensile strength testing on every production batch.' },
      ],
      whyItems: [
        { icon: 'bi bi-gem', title: 'Uncompromising Raw Materials', desc: 'We exclusively use 100% Virgin HDPE and PVC, providing better flexibility and a longer lifespan than recycled competition.' },
        { icon: 'bi bi-sliders', title: 'Tailored Versatility', desc: 'Massive range of weights (125 to 750+ GSM) and thicknesses (up to 1000 Gauge) — the exact solution for your specific load.' },
        { icon: 'bi bi-link-45deg', title: 'Reinforced by Design', desc: 'Unique "Every 1 Meter" eyelet placement and double-corner reinforcement ensure your cover stays put, even in high-wind zones.' },
        { icon: 'bi bi-bag-check-fill', title: 'Complete Protection Ecosystem', desc: 'From tarpaulins to KeBee Nylon Ropes — a one-stop-shop for total asset protection and security.' },
        { icon: 'bi bi-currency-rupee', title: 'Direct-to-Customer Value', desc: 'Premium-grade specifications at competitive prices — the best "protection-per-square-foot" in the industry.' },
      ],
    }
  },
  computed: {
    recentBlogs() {
      return blogs.slice(0, 3)
    }
  }
}
</script>

<style scoped>
/* ── Hero ── */
.kb-hero {
  background: #F0F4F8;
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding-top: 80px; /* offset fixed header */
  overflow: hidden;
  position: relative;
}
/* subtle background texture */
.kb-hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 60% 60% at 80% 50%, rgba(245,158,11,0.07) 0%, transparent 70%),
    radial-gradient(ellipse 40% 50% at 10% 80%, rgba(15,31,61,0.05) 0%, transparent 60%);
  pointer-events: none;
}
.kb-hero__inner {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  padding-top: 60px;
  padding-bottom: 80px;
}

/* LEFT */
.kb-hero__left {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
.kb-hero__badge {
  display: inline-flex;
  align-items: center;
  background: rgba(245,158,11,0.12);
  border: 1.5px solid rgba(245,158,11,0.35);
  color: #B45309;
  font-size: 0.78rem;
  font-weight: 700;
  padding: 6px 16px;
  border-radius: 50px;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  margin-bottom: 1.5rem;
}
.kb-hero__left h1 {
  font-family: 'Poppins', sans-serif;
  font-size: clamp(2.4rem, 4.5vw, 3.6rem);
  font-weight: 800;
  color: #0F1F3D;
  line-height: 1.12;
  margin-bottom: 1.25rem;
  letter-spacing: -0.02em;
}
.kb-hero__left h1 span {
  color: #F59E0B;
  position: relative;
}
/* underline accent on span */
.kb-hero__left h1 span::after {
  content: '';
  position: absolute;
  left: 0; right: 0;
  bottom: -4px;
  height: 4px;
  background: #F59E0B;
  border-radius: 2px;
  opacity: 0.45;
}
.kb-hero__left p {
  font-size: 1.05rem;
  color: #4B5563;
  line-height: 1.8;
  max-width: 480px;
  margin-bottom: 2rem;
}
.kb-hero__actions {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 2.5rem;
}
/* Product pills */
.kb-hero__pills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.kb-hero__pills span {
  display: inline-flex;
  align-items: center;
  background: #fff;
  color: #374151;
  font-size: 0.82rem;
  font-weight: 500;
  padding: 6px 14px;
  border-radius: 50px;
  box-shadow: 0 2px 8px rgba(15,31,61,0.08);
  border: 1px solid #E5E7EB;
}
.kb-hero__pills span .bi {
  color: #10B981;
  font-size: 0.85rem;
}

/* RIGHT */
.kb-hero__right {
  position: relative;
  display: flex;
  justify-content: center;
}
.kb-hero__img-frame {
  position: relative;
  width: 100%;
  max-width: 560px;
}
/* decorative blobs */
.kb-hero__blob {
  position: absolute;
  border-radius: 50%;
  z-index: 0;
}
.kb-hero__blob--1 {
  width: 380px;
  height: 380px;
  background: radial-gradient(circle, rgba(245,158,11,0.18) 0%, transparent 70%);
  top: -40px;
  right: -40px;
}
.kb-hero__blob--2 {
  width: 260px;
  height: 260px;
  background: radial-gradient(circle, rgba(15,31,61,0.1) 0%, transparent 70%);
  bottom: 0;
  left: -30px;
}
.kb-hero__img-frame img {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 480px;
  object-fit: cover;
  object-position: center top;
  border-radius: 32px;
  box-shadow: 0 24px 64px rgba(15,31,61,0.18), 0 8px 24px rgba(15,31,61,0.1);
}
/* floating badge */
.kb-hero__float-badge {
  position: absolute;
  bottom: 28px;
  left: -20px;
  z-index: 2;
  background: #fff;
  border-radius: 14px;
  padding: 12px 18px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 8px 32px rgba(15,31,61,0.14);
}
.kb-hero__float-badge .bi {
  font-size: 1.6rem;
  color: #10B981;
}
.kb-hero__float-badge strong {
  display: block;
  font-size: 0.85rem;
  font-weight: 700;
  color: #0F1F3D;
  line-height: 1;
  margin-bottom: 2px;
}
.kb-hero__float-badge span {
  font-size: 0.72rem;
  color: #9CA3AF;
}

@media (max-width: 992px) {
  .kb-hero__inner {
    grid-template-columns: 1fr;
    gap: 3rem;
    padding-bottom: 60px;
  }
  .kb-hero__right { order: -1; }
  .kb-hero__img-frame img { height: 320px; }
  .kb-hero__float-badge { left: 12px; bottom: 16px; }
  .kb-hero__left h1 { font-size: clamp(2rem, 6vw, 2.8rem); }
}
@media (max-width: 576px) {
  .kb-hero { padding-top: 70px; }
  .kb-hero__trust { flex-wrap: wrap; justify-content: center; }
  .kb-hero__img-frame img { height: 240px; border-radius: 20px; }
}

/* ── Stats ── */
.kb-stats {
  background: #0F1F3D;
  padding: 0;
}
.kb-stats__grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
}
.kb-stats__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 20px;
  border-right: 1px solid rgba(255,255,255,0.07);
  transition: background 0.2s;
}
.kb-stats__item:last-child { border-right: none; }
.kb-stats__item:hover { background: rgba(245,158,11,0.08); }
.kb-stats__icon {
  font-size: 1.6rem;
  color: #F59E0B;
  margin-bottom: 8px;
}
.kb-stats__num {
  font-family: 'Poppins', sans-serif;
  font-size: 1.8rem;
  font-weight: 700;
  color: #fff;
}
.kb-stats__label {
  font-size: 0.8rem;
  color: rgba(255,255,255,0.55);
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

/* ── Products Grid ── */
.kb-products-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.75rem;
  margin-top: 2.5rem;
}
.kb-product-card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(15,31,61,0.07);
  transition: transform 0.3s, box-shadow 0.3s;
}
.kb-product-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 40px rgba(15,31,61,0.14);
}
.kb-product-card__img {
  position: relative;
  height: 220px;
  overflow: hidden;
}
.kb-product-card__img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s;
}
.kb-product-card:hover .kb-product-card__img img {
  transform: scale(1.05);
}
.kb-product-card__badge {
  position: absolute;
  top: 14px;
  right: 14px;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 50px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}
.kb-product-card__body {
  padding: 1.5rem;
}
.kb-product-card__cat {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #F59E0B;
}
.kb-product-card__body h3 {
  font-family: 'Poppins', sans-serif;
  font-size: 1.1rem;
  font-weight: 700;
  color: #0F1F3D;
  margin: 6px 0 10px;
}
.kb-product-card__body p {
  font-size: 0.88rem;
  color: #6B7280;
  line-height: 1.6;
  margin-bottom: 1rem;
}
.kb-product-card__link {
  font-size: 0.88rem;
  font-weight: 600;
  color: #0F1F3D;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  transition: color 0.2s;
}
.kb-product-card__link:hover { color: #F59E0B; }

/* ── About Home ── */
.kb-about-home {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  align-items: center;
}
.kb-about-home__images {
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}
.kb-about-home__img-main {
  grid-row: span 2;
}
.kb-about-home__img-main img,
.kb-about-home__img-sub img {
  width: 100%;
  border-radius: 16px;
  object-fit: cover;
  height: 100%;
}
.kb-about-home__img-main { height: 380px; }
.kb-about-home__img-sub {
  position: relative;
  height: 180px;
}
.kb-about-home__img-badge {
  position: absolute;
  bottom: -20px;
  left: 50%;
  transform: translateX(-50%);
  background: #F59E0B;
  color: #fff;
  padding: 14px 20px;
  border-radius: 12px;
  text-align: center;
  min-width: 100px;
}
.kb-about-home__img-badge .num {
  display: block;
  font-family: 'Poppins', sans-serif;
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 1;
}
.kb-about-home__img-badge .lbl {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  line-height: 1.3;
}
.kb-about-home__content .lead-text {
  font-style: italic;
  color: #374151;
  margin-bottom: 1rem;
  font-size: 1rem;
  line-height: 1.7;
}
.kb-about-home__content p {
  color: #6B7280;
  line-height: 1.8;
  margin-bottom: 1.5rem;
}
.kb-about-home__pillars {
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
}
.kb-pillar {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}
.kb-pillar__icon {
  width: 42px;
  height: 42px;
  background: rgba(245,158,11,0.1);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  color: #F59E0B;
  flex-shrink: 0;
}
.kb-pillar h4 {
  font-family: 'Poppins', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  color: #0F1F3D;
  margin: 0 0 4px;
}
.kb-pillar p {
  font-size: 0.85rem;
  color: #6B7280;
  margin: 0;
  line-height: 1.6;
}

/* ── Why Grid ── */
.kb-why-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-top: 2.5rem;
}
.kb-why-card {
  background: #fff;
  border-radius: 16px;
  padding: 2rem 1.75rem;
  box-shadow: 0 2px 16px rgba(15,31,61,0.06);
  position: relative;
  overflow: hidden;
  transition: transform 0.3s, box-shadow 0.3s;
}
.kb-why-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 32px rgba(15,31,61,0.12);
}
.kb-why-card__num {
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  font-family: 'Poppins', sans-serif;
  font-size: 2.5rem;
  font-weight: 800;
  color: rgba(15,31,61,0.04);
  line-height: 1;
}
.kb-why-card__icon {
  font-size: 1.6rem;
  color: #F59E0B;
  margin-bottom: 1rem;
}
.kb-why-card h3 {
  font-family: 'Poppins', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  color: #0F1F3D;
  margin-bottom: 0.75rem;
}
.kb-why-card p {
  font-size: 0.875rem;
  color: #6B7280;
  line-height: 1.7;
  margin: 0;
}

/* ── Blog Preview ── */
.kb-blog-preview {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.75rem;
  margin-top: 2.5rem;
}
.kb-blog-card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(15,31,61,0.07);
  transition: transform 0.3s, box-shadow 0.3s;
}
.kb-blog-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 36px rgba(15,31,61,0.13);
}
.kb-blog-card__img {
  position: relative;
  height: 200px;
  overflow: hidden;
}
.kb-blog-card__img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s;
}
.kb-blog-card:hover .kb-blog-card__img img { transform: scale(1.05); }
.kb-blog-card__cat {
  position: absolute;
  top: 14px;
  left: 14px;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 50px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.kb-blog-card__body {
  padding: 1.5rem;
}
.kb-blog-card__meta {
  display: flex;
  gap: 1rem;
  font-size: 0.78rem;
  color: #9CA3AF;
  margin-bottom: 0.75rem;
}
.kb-blog-card__body h3 {
  font-family: 'Poppins', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  color: #0F1F3D;
  margin-bottom: 0.75rem;
  line-height: 1.4;
}
.kb-blog-card__body p {
  font-size: 0.85rem;
  color: #6B7280;
  line-height: 1.6;
  margin-bottom: 1rem;
}
.kb-blog-card__link {
  font-size: 0.85rem;
  font-weight: 600;
  color: #0F1F3D;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  transition: color 0.2s;
}
.kb-blog-card__link:hover { color: #F59E0B; }

/* ── CTA Banner ── */
.kb-cta-banner {
  background: linear-gradient(135deg, #0F1F3D 0%, #1E3A6E 100%);
  padding: 80px 0;
  text-align: center;
}
.kb-cta-banner__content h2 {
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.5rem, 3vw, 2.25rem);
  font-weight: 700;
  color: #fff;
  margin-bottom: 1rem;
}
.kb-cta-banner__content p {
  color: rgba(255,255,255,0.75);
  font-size: 1.05rem;
  margin-bottom: 2rem;
}
.kb-cta-banner__actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

/* ── Contact Strip ── */
.kb-contact-strip {
  background: #F9FAFB;
  padding: 40px 0;
  border-top: 1px solid #E5E7EB;
}
.kb-contact-strip__grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
}
.kb-contact-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  text-decoration: none;
  color: inherit;
  padding: 16px;
  border-radius: 12px;
  transition: background 0.2s;
}
.kb-contact-item:hover { background: #fff; }
.kb-contact-item i {
  font-size: 1.6rem;
  color: #F59E0B;
  flex-shrink: 0;
}
.kb-contact-item span {
  display: block;
  font-size: 0.78rem;
  color: #9CA3AF;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.kb-contact-item strong {
  display: block;
  font-family: 'Poppins', sans-serif;
  font-size: 0.95rem;
  color: #0F1F3D;
}

/* ── Responsive ── */
@media (max-width: 992px) {
  .kb-stats__grid { grid-template-columns: repeat(2, 1fr); }
  .kb-products-grid { grid-template-columns: repeat(2, 1fr); }
  .kb-about-home { grid-template-columns: 1fr; gap: 3rem; }
  .kb-why-grid { grid-template-columns: repeat(2, 1fr); }
  .kb-blog-preview { grid-template-columns: repeat(2, 1fr); }
  .kb-contact-strip__grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 576px) {
  .kb-stats__grid { grid-template-columns: repeat(2, 1fr); }
  .kb-products-grid { grid-template-columns: 1fr; }
  .kb-why-grid { grid-template-columns: 1fr; }
  .kb-blog-preview { grid-template-columns: 1fr; }
  .kb-contact-strip__grid { grid-template-columns: 1fr; }
  .kb-about-home__images { grid-template-columns: 1fr; }
  .kb-about-home__img-main { height: 240px; }
  .kb-about-home__img-sub { display: none; }
  .kb-hero__actions { flex-direction: column; align-items: stretch; }
  .kb-hero__actions .kb-btn { text-align: center; justify-content: center; }
}
</style>
